import { memo } from 'react'

import {
  AppSettingsAlt,
  Assignment,
  Event,
  Handshake,
  FilterNone,
  MenuBook,
  SpaceDashboard,
  Tv,
  Vibration,
  Videocam,
  Settings,
  Category,
} from '@mui/icons-material'
import { Box, ListItemText } from '@mui/material'
import { useMatchRoute } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { collapseAnimationFactory } from '@shared/components/Collapse'
import { ListItemButtonLink } from 'components/LinkedMUIComponents'
import {
  itemVariants,
  listVariants,
  NavLinkListItem,
  NavLinkListItemTextPendable,
  NavLinkStartIcon,
} from 'domains/_Sidebar'
import { NavigationList } from 'layouts'
import {
  mediaPlatformAssetsOverviewRoute,
  mediaPlatformAssetsRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets'
import {
  mediaPlatformBundlesOverviewRoute,
  mediaPlatformBundlesRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Bundles'
import {
  mediaPlatformChannelsOverviewRoute,
  mediaPlatformChannelsRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Channels'
import {
  mediaPlatformEventsOverviewRoute,
  mediaPlatformEventsRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Events'
import {
  mediaPlatformIngestTasksOverviewRoute,
  mediaPlatformIngestTasksRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/IngestTasks'
import {
  mediaPlatformOffersOverviewRoute,
  mediaPlatformOffersRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Offers'
import {
  mediaPlatformPagesOverviewRoute,
  mediaPlatformPagesRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Pages'
import { mediaPlatformSettingsOverviewRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Settings'
import {
  mediaPlatformSetupOverviewRoute,
  mediaPlatformSetupRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Setup'
import {
  mediaPlatformShortsOverviewRoute,
  mediaPlatformShortsRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Shorts'
import {
  mediaPlatformSpacesOverviewRoute,
  mediaPlatformSpacesRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Spaces'
import {
  mediaPlatformVideosOverviewRoute,
  mediaPlatformVideosRoute,
} from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Videos'

type ItemsProps = {
  organizationId: string
  environmentId: string
  platformId: string
}

const ItemsRaw = ({ organizationId, environmentId, platformId }: ItemsProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])

  const matchRoute = useMatchRoute()

  return (
    <Box component={motion.div} variants={collapseAnimationFactory({ opacity: 1 }, { opacity: 1 })}>
      <NavigationList component={motion.ul} variants={listVariants} initial="start" animate="show" exit="exit">
        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformAssetsOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformAssetsRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Category />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.asset', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformSetupOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformSetupRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <AppSettingsAlt />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.setup', { count: 1 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformPagesOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformPagesRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <MenuBook />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.page', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformChannelsOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformChannelsRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Tv />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>
                  {t('entity:publishing.channel', { count: 2 })}
                </NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformEventsOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformEventsRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Event />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.event', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformShortsOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformShortsRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Vibration />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.short', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformVideosOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformVideosRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Videocam />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.video', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformBundlesOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformBundlesRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <FilterNone />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.bundle', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformIngestTasksOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformIngestTasksRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Assignment />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>
                  {t('entity:publishing.ingestTask', { count: 2 })}
                </NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformSpacesOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformSpacesRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <SpaceDashboard />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.space', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformOffersOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformOffersRoute.to, fuzzy: true })}
          >
            <NavLinkStartIcon>
              <Handshake />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:publishing.offer', { count: 2 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <NavLinkListItem disablePadding component={motion.li} variants={itemVariants} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={mediaPlatformSettingsOverviewRoute.to}
            params={{
              organizationId,
              environmentId,
              platformId,
            }}
            selected={!!matchRoute({ to: mediaPlatformSettingsOverviewRoute.to })}
          >
            <NavLinkStartIcon>
              <Settings />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={<NavLinkListItemTextPendable>{t('entity:publishing.settings')}</NavLinkListItemTextPendable>}
            />
          </ListItemButtonLink>
        </NavLinkListItem>
      </NavigationList>
    </Box>
  )
}

export const Items = memo(ItemsRaw)
